import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import LinearProgress from '@mui/material/LinearProgress'
import Chip from '@mui/material/Chip'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

import { GanttBar, pct } from '../components/GanttBar'
import { StatusChip } from '../components/StatusChip'
import { COMMON, FEEDS_SHORT, REFRESH_VIEW, TODAY_VIEW } from '../labels'
import { longDate, relativeDays, shortDate } from '../lib/format'
import type { Alert as AlertT, CycleView, Summary } from '../lib/types'
import {
  ACCENT,
  ACCENT_SOFT,
  BAND,
  BAND_SOFT,
  FONT_MONO,
  INK,
  INK_FAINT,
  INK_SOFT,
  RISK_COLOR,
  SEVERITY_COLOR,
  TINT,
  monoLabel,
  serifNumber,
} from '../theme'

interface Props {
  summary: Summary
  alerts: AlertT[]
  cycles: CycleView[]
}

/**
 * La vista de entrada, ordenada por la prioridad que fijó el brief:
 *   1. qué requiere atención hoy
 *   2. qué está atrasado o en riesgo
 *   3. qué viene
 *   4. cómo vamos contra el calendario
 *
 * El titular existe para que la respuesta llegue antes que la lectura: desde
 * la puerta se ve si hoy hay algo que hacer. Si no lo hay, el panel de
 * atención lo dice y se quita de en medio, en vez de rellenar con KPIs para
 * verse ocupado.
 */
export function TodayView({ summary, alerts, cycles }: Props) {
  return (
    <Stack spacing={4}>
      <Hero summary={summary} count={alerts.length} />

      <Freshness summary={summary} />

      {alerts.length > 0 && <NeedsAttention alerts={alerts} />}

      {summary.next_pricing_refresh && (
        <RefreshStrip
          cycles={cycles.filter((c) => c.kind === 'pricing_refresh')}
          today={summary.today}
        />
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2.5 }}>
        <NextMilestones summary={summary} />
        <ActiveCycles cycles={cycles} today={summary.today} />
      </Box>
    </Stack>
  )
}

/**
 * Titular editorial sobre un lavado de color.
 *
 * El fondo del panel es la primera señal, antes que cualquier palabra: rosado
 * si hay algo pendiente, salvia si no. Se ve desde la puerta, sin leer.
 */
function Hero({ summary, count }: { summary: Summary; count: number }) {
  const { count: head, body, tail } = TODAY_VIEW.hero(count)

  const refresh = summary.next_pricing_refresh
  const stats: [string, boolean][] = [
    [TODAY_VIEW.statDelayed(summary.delayed_sources.length), summary.delayed_sources.length > 0],
    [TODAY_VIEW.statAtRisk(summary.cycles_at_risk.length), summary.cycles_at_risk.length > 0],
    ...(refresh
      ? ([
          [
            `${TODAY_VIEW.statBuffer} ${refresh.buffer_remaining}/${refresh.buffer_days}`,
            refresh.buffer_remaining === 0,
          ],
        ] as [string, boolean][])
      : []),
    [
      summary.last_checked_at
        ? `${TODAY_VIEW.statChecked} ${relativeDays(summary.last_checked_at.slice(0, 10), summary.today)}`
        : TODAY_VIEW.statNeverChecked,
      !summary.last_checked_at,
    ],
  ]

  return (
    <Box
      sx={{
        bgcolor: count > 0 ? TINT.blush : TINT.sage,
        border: `1px solid ${count > 0 ? '#F1DCD4' : '#DCE4CE'}`,
        borderRadius: '24px',
        px: { xs: 3, md: 4.5 },
        py: { xs: 3.5, md: 4.5 },
      }}
    >
      <Typography
        component="p"
        sx={{
          ...serifNumber,
          fontSize: 'clamp(2.5rem, 6.4vw, 4.25rem)',
          lineHeight: 0.95,
          maxWidth: '15ch',
          textWrap: 'balance',
        }}
      >
        {head && <Box component="span" sx={{ color: ACCENT }}>{head}</Box>}
        {body}
        <Box component="span" sx={{ fontStyle: 'italic' }}>
          {tail}
        </Box>
      </Typography>

      {count === 0 && (
        <Typography sx={{ mt: 2, color: INK_SOFT, fontSize: '0.9375rem', maxWidth: '52ch' }}>
          {TODAY_VIEW.heroCalm}
        </Typography>
      )}

      <Stack
        direction="row"
        flexWrap="wrap"
        useFlexGap
        alignItems="center"
        sx={{
          mt: 3,
          pt: 2.25,
          borderTop: `1px solid ${count > 0 ? '#EDD3CA' : '#D5DEC4'}`,
          gap: 0,
          rowGap: 1,
        }}
      >
        {stats.map(([text, hot], i) => (
          <Box key={i} sx={{ display: 'flex', alignItems: 'center' }}>
            {i > 0 && (
              <Box component="span" sx={{ color: INK_FAINT, px: 1.5, fontSize: '0.75rem' }}>
                ·
              </Box>
            )}
            <Box
              component="span"
              sx={{
                fontFamily: FONT_MONO,
                fontSize: '0.6875rem',
                fontWeight: hot ? 600 : 500,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: hot ? ACCENT : INK_SOFT,
              }}
            >
              {text}
            </Box>
          </Box>
        ))}
      </Stack>
    </Box>
  )
}

function NeedsAttention({ alerts }: { alerts: AlertT[] }) {
  return (
    <Box>
      <SectionTitle title={TODAY_VIEW.needsAttention} count={alerts.length} />
      <Stack spacing={1.25}>
        {alerts.map((a) => (
          <AlertRow key={a.dedupe_key} alert={a} />
        ))}
      </Stack>
    </Box>
  )
}

/** Qué pasó → qué impacto → qué acción → quién: las cuatro piezas, siempre. */
function AlertRow({ alert }: { alert: AlertT }) {
  const color = SEVERITY_COLOR[alert.severity] ?? '#A6998A'
  return (
    <Paper
      sx={{
        display: 'flex',
        gap: 2,
        p: 2.25,
        pl: 2.5,
        // Barra de color a la izquierda: la severidad se ve antes de leer.
        borderLeft: `5px solid ${color}`,
        transition: 'transform 180ms cubic-bezier(0.2,0.8,0.2,1), box-shadow 180ms',
        '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 4px 0 rgba(21,18,16,0.08)' },
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="h3" sx={{ mb: 0.75 }}>
          {alert.title}
        </Typography>
        <Typography variant="body2" sx={{ color: INK_SOFT, mb: 0.75 }}>
          {alert.impact}
        </Typography>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'baseline',
            gap: 0.75,
            bgcolor: BAND,
            borderRadius: 999,
            px: 1.5,
            py: 0.6,
          }}
        >
          <Box
            component="span"
            sx={{
              fontFamily: FONT_MONO,
              fontSize: '0.625rem',
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              color: INK_FAINT,
            }}
          >
            {TODAY_VIEW.action}
          </Box>
          <Typography variant="body2" sx={{ color: INK, fontWeight: 500 }}>
            {alert.action}
          </Typography>
        </Box>
      </Box>
      <Stack spacing={1} sx={{ alignItems: 'flex-end', flexShrink: 0 }}>
        <StatusChip value={alert.severity} kind="severity" />
        <Typography
          sx={{
            fontFamily: FONT_MONO,
            fontSize: '0.6875rem',
            color: INK_FAINT,
            textAlign: 'right',
          }}
        >
          {alert.owner}
        </Typography>
      </Stack>
    </Paper>
  )
}

/** La ventana completa como cinta, no como una fecha suelta. */
function RefreshStrip({ cycles, today }: { cycles: CycleView[]; today: string }) {
  const cycle = cycles[0]
  if (!cycle) return null

  const { window_start, deadline } = cycle
  const done = cycle.stages.filter((s) => s.actual_finish)
  const lastDone = done.length ? done[done.length - 1].actual_finish! : window_start

  return (
    <Paper sx={{ p: 3 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ sm: 'center' }}
        spacing={1}
        sx={{ mb: 2.5 }}
      >
        <Typography variant="h2">{cycle.cycle_name}</Typography>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <StatusChip value={cycle.risk} kind="risk" />
          <Typography
            sx={{
              fontFamily: FONT_MONO,
              fontSize: '0.6875rem',
              letterSpacing: '0.04em',
              color: INK_SOFT,
            }}
          >
            {REFRESH_VIEW.deadline}: {longDate(deadline)} · {relativeDays(deadline, today)}
          </Typography>
        </Stack>
      </Stack>

      <GanttBar
        height={34}
        segments={[
          {
            start: 0,
            end: pct(lastDone, window_start, deadline),
            color: cycle.color,
            label: `${Math.round(cycle.progress * 100)}%`,
            tooltip: `${REFRESH_VIEW.progress}: ${Math.round(cycle.progress * 100)}%`,
          },
          {
            start: pct(cycle.last_allowed_finish, window_start, deadline),
            end: 100,
            color: RISK_COLOR[cycle.risk] ?? '#DE8A1B',
            label: `${REFRESH_VIEW.buffer} ${cycle.buffer_remaining}/${cycle.buffer_days}`,
            striped: true,
            tooltip: `${REFRESH_VIEW.bufferRemaining}: ${cycle.buffer_remaining} de ${cycle.buffer_days} días hábiles`,
          },
        ]}
        markers={[
          { at: pct(today, window_start, deadline), color: INK, label: COMMON.today, solid: true },
          {
            at: pct(cycle.projected_finish, window_start, deadline),
            color: RISK_COLOR[cycle.risk],
            label: `${REFRESH_VIEW.projected}: ${longDate(cycle.projected_finish)}`,
          },
        ]}
      />

      <Stack direction="row" justifyContent="space-between" sx={{ mt: 1.25 }}>
        <Axis>{longDate(window_start)}</Axis>
        <Axis>{longDate(deadline)}</Axis>
      </Stack>

      {cycle.blocking_source && (
        <Alert severity="warning" sx={{ mt: 2.5 }}>
          {REFRESH_VIEW.blockedBy}: <b>{cycle.blocking_source}</b>
        </Alert>
      )}
    </Paper>
  )
}

function NextMilestones({ summary }: { summary: Summary }) {
  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h2" sx={{ mb: 2 }}>
        {TODAY_VIEW.nextMilestones}
      </Typography>
      {summary.next_milestones.length === 0 ? (
        <Axis>{TODAY_VIEW.noMilestones}</Axis>
      ) : (
        <Stack divider={<Divider />}>
          {summary.next_milestones.map((m, i) => (
            <Stack
              key={i}
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ py: 1.25 }}
            >
              <Box sx={{ minWidth: 0, pr: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {m.source}
                </Typography>
                <Axis>{m.period}</Axis>
              </Box>
              <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                <Typography sx={{ ...serifNumber, fontSize: '1.25rem' }}>
                  {shortDate(m.date)}
                </Typography>
                <Axis>{relativeDays(m.date, summary.today)}</Axis>
              </Box>
            </Stack>
          ))}
        </Stack>
      )}
    </Paper>
  )
}

function ActiveCycles({ cycles, today }: { cycles: CycleView[]; today: string }) {
  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h2" sx={{ mb: 2 }}>
        {TODAY_VIEW.activeCycles}
      </Typography>
      <Stack spacing={2.25}>
        {cycles.map((c) => (
          <Box key={`${c.cycle_id}-${c.data_period}`}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {c.cycle_name}
              </Typography>
              <StatusChip value={c.risk} kind="risk" />
            </Stack>
            <Axis>
              {c.data_period} · {REFRESH_VIEW.deadline} {shortDate(c.deadline)} ·{' '}
              {relativeDays(c.deadline, today)}
            </Axis>
            <LinearProgress
              variant="determinate"
              value={c.progress * 100}
              sx={{
                mt: 1,
                height: 6,
                '& .MuiLinearProgress-bar': { bgcolor: c.color },
              }}
            />
          </Box>
        ))}
      </Stack>
    </Paper>
  )
}

/**
 * Con qué datos está cada producto.
 *
 * La pregunta que se hace antes de usar cualquier cifra del dashboard o de la
 * herramienta. Sale de los ciclos CERRADOS, no de un campo que alguien tenga
 * que acordarse de mover: un ciclo cerrado para el mes X significa exactamente
 * "este producto está actualizado hasta X".
 *
 * Se separan "en curso" de "sin empezar" a propósito. En un número suelto se
 * leen igual, y para quien va a decidir algo no lo son.
 */
function Freshness({ summary }: { summary: Summary }) {
  const filas = summary.freshness ?? []
  if (filas.length === 0) return null
  return (
    <Box>
      <SectionTitle title={TODAY_VIEW.freshness} />
      <Typography sx={{ ...monoLabel, color: INK_FAINT, mb: 2, textTransform: 'none' }}>
        {TODAY_VIEW.freshnessHint}
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(auto-fit, minmax(240px, 1fr))' },
          gap: 2,
        }}
      >
        {filas.map((f) => (
          <Paper key={f.cycle_id} sx={{ p: 2.5 }}>
            <Box component="span" sx={{ ...monoLabel, display: 'block', color: INK_FAINT }}>
              {f.cycle_name}
            </Box>
            <Box
              component="span"
              sx={{
                ...serifNumber,
                fontSize: '1.75rem',
                display: 'block',
                mt: 0.75,
                color: f.up_to ? INK : INK_FAINT,
              }}
            >
              {f.up_to_label ?? TODAY_VIEW.freshnessNever}
            </Box>
            <Stack direction="row" spacing={0.75} sx={{ mt: 1.25 }} flexWrap="wrap" useFlexGap>
              {f.feeds.map((p) => (
                <Chip key={p} size="small" variant="outlined" label={FEEDS_SHORT[p] ?? p} />
              ))}
              {f.in_progress && (
                <Chip
                  size="small"
                  label={`${f.in_progress} ${TODAY_VIEW.freshnessInProgress}`}
                  sx={{ bgcolor: BAND_SOFT }}
                />
              )}
              {f.not_started && (
                <Chip
                  size="small"
                  label={`${f.not_started} ${TODAY_VIEW.freshnessNotStarted}`}
                  sx={{ bgcolor: ACCENT_SOFT, color: ACCENT }}
                />
              )}
            </Stack>
          </Paper>
        ))}
      </Box>
    </Box>
  )
}

function SectionTitle({ title, count }: { title: string; count?: number }) {
  return (
    <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mb: 1.5 }}>
      <Typography variant="h2">{title}</Typography>
      {count !== undefined && (
        <Box
          component="span"
          sx={{ ...serifNumber, fontSize: '1.125rem', color: INK_FAINT }}
        >
          {String(count).padStart(2, '0')}
        </Box>
      )}
    </Stack>
  )
}

/** Texto de eje/pie: mono, gris, siempre el mismo tamaño. */
function Axis({ children }: { children: React.ReactNode }) {
  return (
    <Box
      component="span"
      sx={{
        display: 'block',
        fontFamily: FONT_MONO,
        fontSize: '0.6875rem',
        color: INK_FAINT,
        letterSpacing: '0.02em',
      }}
    >
      {children}
    </Box>
  )
}
